import { useCallback, useEffect, useState, type ReactNode } from "react";
import { authApi } from "../api/auth";
import { tokenStore } from "../lib/tokenStore";
import type { Admin } from "../types/admin";
import { AuthContext, type AuthContextValue } from "./AuthContext";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [admin, setAdmin] = useState<Admin | null>(() =>
    tokenStore.getProfile(),
  );
  const [loading, setLoading] = useState(true);

  // On boot, if we hold a token, confirm it by fetching the live profile.
  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      if (!tokenStore.getAccess()) {
        setLoading(false);
        return;
      }
      try {
        const { admin: fresh } = await authApi.profile();
        if (!cancelled) {
          tokenStore.setProfile(fresh);
          setAdmin(fresh);
        }
      } catch {
        // 401 path already clears tokens via the axios interceptor.
        if (!cancelled) setAdmin(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const result = await authApi.login(email, password);
    tokenStore.setTokens(result.tokens.accessToken, result.tokens.refreshToken);
    tokenStore.setProfile(result.admin);
    setAdmin(result.admin);
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    tokenStore.clear();
    setAdmin(null);
  }, []);

  const value: AuthContextValue = {
    admin,
    isAuthenticated: !!admin,
    loading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
